/**
 * AppShellWithSidebar — owija stronę głównym sidebarem aplikacji.
 *
 * Aplikacja nie ma globalnego layoutu (z założenia historycznego — chat-first).
 * Strony admin (autopilot, firm-assets) były standalone bez nawigacji, co dezorientowało
 * userów wracających z dashboardu. Ten komponent reuse-uje pattern z `monitoring.tsx`:
 * sticky sidebar + main content + mobile-friendly toggle.
 */
import { useState, type ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, Menu, X } from "lucide-react";
import Sidebar from "@/components/sidebar";

interface AppShellWithSidebarProps {
  children: ReactNode;
}

export function AppShellWithSidebar({ children }: AppShellWithSidebarProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true);

  return (
    <div className="flex h-full bg-[#030407] relative overflow-hidden">
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="hidden md:flex fixed top-4 left-4 z-50 p-3 bg-[#0a0c10]/90 backdrop-blur-md text-white rounded-xl hover:bg-[#12151c]/90 transition-all duration-200 shadow-lg border border-white/[0.08] btn-modern"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <AnimatePresence mode="wait">
          {sidebarOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronLeft size={20} />
            </motion.div>
          ) : (
            <motion.div
              key="open"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Menu size={20} />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ x: -320, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -320, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30, opacity: { duration: 0.2 } }}
            className="fixed md:relative inset-y-0 left-0 z-40 w-72 lg:w-80 h-full bg-[#0a0c10] flex flex-col overscroll-behavior-none glass-morphism border-r border-white/[0.08]"
          >
            <Sidebar />
            <button
              onClick={() => setSidebarOpen(false)}
              className="absolute top-4 right-4 md:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="flex-1 flex flex-col min-w-0 relative overflow-hidden h-full">
        <div className="md:hidden sticky top-0 z-10 flex items-center justify-between p-4 bg-[#0a0c10] border-b border-white/[0.08]">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <Menu size={20} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-hidden">{children}</div>
      </main>
    </div>
  );
}
